import { z } from "zod";
import type { FilterOperator, FilterValue } from "@/lib/erp/filterTypes";

/**
 * Validation schema for FilterOperator enum
 */
export const filterOperatorSchema = z.enum([
  "equals",
  "notEquals",
  "like",
  "notLike",
  "in",
  "notIn",
  "greaterThan",
  "lessThan",
  "greaterOrEqual",
  "lessOrEqual",
  "between",
  "isSet",
  "isNotSet",
  "is",
  "timespan",
]);

/**
 * Validation schema for field names
 * - Must be non-empty string
 * - Max 100 characters (reasonable limit)
 * - Only alphanumeric, underscore, and dot characters (Frappe field naming convention)
 */
export const fieldNameSchema = z
  .string()
  .min(1, "Field name cannot be empty")
  .max(100, "Field name cannot exceed 100 characters")
  .regex(
    /^[a-zA-Z0-9_.]+$/,
    "Field name can only contain letters, numbers, underscores, and dots"
  );

/**
 * Validation schema for filter values
 * Supports all FilterValue types
 */
export const filterValueSchema: z.ZodType<FilterValue> = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.array(z.string()),
  z.array(z.number()),
  z.tuple([z.number(), z.number()]), // for between numbers
  z.tuple([z.string(), z.string()]), // for between dates
  z.null(),
]);

/**
 * Validation schema for FilterDescriptor
 */
export const filterDescriptorSchema = z.object({
  field: fieldNameSchema,
  operator: filterOperatorSchema,
  value: filterValueSchema,
});

/**
 * Validation schema for array of FilterDescriptors
 */
export const filterArraySchema = z.array(filterDescriptorSchema);

/**
 * Validate a single filter descriptor
 * @param filter - Filter descriptor to validate
 * @returns Validation result with success status and errors if any
 */
export function validateFilterDescriptor(
  filter: unknown
): { success: boolean; error?: z.ZodError } {
  const result = filterDescriptorSchema.safeParse(filter);
  if (result.success) {
    return { success: true };
  }
  return { success: false, error: result.error };
}

/**
 * Validate an array of filter descriptors
 * @param filters - Array of filter descriptors to validate
 * @returns Validation result with success status and errors if any
 */
export function validateFilterArray(
  filters: unknown
): { success: boolean; error?: z.ZodError } {
  const result = filterArraySchema.safeParse(filters);
  if (result.success) {
    return { success: true };
  }
  return { success: false, error: result.error };
}

/**
 * Validate filter value based on operator
 * Some operators don't require values (isSet, isNotSet, is)
 * @param operator - Filter operator
 * @param value - Filter value to validate
 * @returns Validation result
 */
export function validateFilterValue(
  operator: FilterOperator,
  value: FilterValue
): { valid: boolean; error?: string } {
  // Operators that don't require values
  if (operator === "isSet" || operator === "isNotSet" || operator === "is") {
    return { valid: true };
  }

  // between operator requires tuple of 2 values
  if (operator === "between") {
    if (!Array.isArray(value) || value.length !== 2) {
      return {
        valid: false,
        error: "Between operator requires an array of exactly 2 values",
      };
    }
    return { valid: true };
  }

  // in/notIn operators require array
  if (operator === "in" || operator === "notIn") {
    if (!Array.isArray(value)) {
      return {
        valid: false,
        error: `${operator} operator requires an array value`,
      };
    }
    if (value.length === 0) {
      return {
        valid: false,
        error: `${operator} operator requires at least one value`,
      };
    }
    return { valid: true };
  }

  // Other operators require a non-null value
  if (value === null || value === undefined || value === "") {
    return {
      valid: false,
      error: `${operator} operator requires a value`,
    };
  }

  return { valid: true };
}

